import React from 'react'
import { Controller } from 'react-hook-form'
import LoxSelect from '../../../../components/LoxSelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { CurrencyFormat } from '../../../../helpers/formatting'
import { reactSelectClassNames } from '../../../../helpers/react-select-styles'
import ManagerApprovalIcons from '../../../../components/elements/ManagerApprovalIcons'
import SpecialInstruction from '../../../../components/elements/SpecialInstruction'

/**
 * Step 1 of the payment-plan wizard. Plan-type selection (Create Collections /
 * Create Settlement / Settle for $0), then a conditional form for entering
 * pay frequency / payment method / first payment date / installment count
 * (collection + settlement) or a confirmation panel (settle-for-zero).
 *
 * Behaviour-preserving extraction from PaymentPlanPayment.jsx. Parent passes
 * `control` from RHF directly so per-cell Controllers stay wired without a
 * mirror layer.
 */
export default function PaymentPlanStep1({
  // Plan-type buttons + outstanding display
  responseData,
  outstandingTotal,
  planSelection,
  strategy,
  // Loan-status flag — when true, only the Resolution button renders.
  // When false (default), the three existing plan-type buttons render.
  // Mutually exclusive in practice: the toolbar gate
  // (PaymentScheduleSubPanel.jsx) only opens the slider when the loan
  // is in collections OR resolution+no_res_plan, never both.
  isInResolutionWithoutPlan,
  getCurrentPaymentInfo,
  checkChangesMade,
  // RHF
  control,
  handleSubmit,
  onSubmit,
  onSettleForZeroSubmit,
  setValue,
  // Pay frequency
  payFrequencyOptions,
  payFrequencyValue,
  setPayFrequencyValue,
  payFrequencyValueOption,
  setPayFrequencyValueOption,
  dayOfWeekOptions,
  dayOfMonthOptions,
  weekOfMonthOptions,
  // Payment method + first payment + discount + installments
  discountData,
  currentDate,
  today,
  collectionPromo,
  settlementDiscountType,
  handleDiscountTypeChange,
  watchCustomerPromotionCodeId,
  discountInfoText,
  setDiscountValue,
  setSettlementDiscountType,
  settlementInstallationType,
  setSettlementInstallationType,
  installmentOptions,
  resolutionMaxPayments,
  minPayment,
  saving,
  // True when the typed settlement discount exceeds the per-balance cap —
  // disables the breakdown controls and Next until the agent corrects it.
  isDiscountAboveCap,
  isBalanceBelowMinimum,
  balanceBelowMinimumText,
}) {
  // Resolution cap helper text; falls back to generic copy before a frequency is picked.
  const resolutionCapTooltip = resolutionMaxPayments
    ? `Resolution plans allow up to ${resolutionMaxPayments} payments for ${payFrequencyValue} frequency — the lesser of 48 months or 200 payments. This is a hard limit with no manager override.`
    : `Resolution plans are limited to the lesser of 48 months at the selected pay frequency or 200 payments. This is a hard limit with no manager override.`
  const breakdownTooltip = planSelection === 'resolution' ? resolutionCapTooltip : `TODO: Helper Text`

  return (
    <>
      <p className="title is-5">Payment Plan Payment - Step 1</p>
      <table className="table is-narrow table-payment-schedule-panel">
        <tbody>
          <tr>
            <td><b>Outstanding Balance</b></td>
            <td><CurrencyFormat value={responseData?.outstanding_total ?? outstandingTotal} /></td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            {/*
              Resolution+no_res_plan loans show only "Create Resolution Plan".
              All other states (collections, etc.) show the existing three
              plan-type buttons. The toolbar gate in PaymentScheduleSubPanel
              ensures the slider is only reachable from one of these two states.
            */}
            {!isInResolutionWithoutPlan && (
              <>
                <td>
                  <div className="is-flex is-align-items-end">
                    <button
                      type="button"
                      data-testid="payment-plan-create-collection"
                      className="button is-link is-small mr-1"
                      onClick={() => getCurrentPaymentInfo("collection")}
                    >
                      <span className="mx-3">Create Collections Plan <span
                        className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                        data-tooltip={`Intention is the customer will pay back the full amount owed. They have a max of two years per payment plan to do so, but they can have multiple payment plans`}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="has-text-light" />
                      </span></span>
                    </button>
                  </div>
                </td>
                <td>
                  <div className="is-flex is-align-items-end">
                    <button
                      type="button"
                      data-testid="payment-plan-create-settlement"
                      className="button is-link is-small mr-1"
                      onClick={() => getCurrentPaymentInfo("settlement")}
                    >
                      <span className="mx-3">Create Settlement Plan <span
                        className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                        data-tooltip={`Intention is to settle for less than the full amount owed. We give a discount with the expectation that is getting paid back in an accelerated timeframe`}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="has-text-light" />
                      </span> <ManagerApprovalIcons type="mgr_approval_status_required" /></span>
                    </button>
                  </div>
                </td>
                <td>
                  <div className="is-flex is-align-items-end">
                    <button
                      type="button"
                      data-testid="payment-plan-settle-for-zero"
                      className="button is-link is-small mr-1"
                      onClick={() => getCurrentPaymentInfo("settleforzero")}
                    >
                      <span className="mx-3">Settle for $0 <span
                        className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                        data-tooltip={`TODO: Helper Text`}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="has-text-light" />
                      </span></span>
                    </button>
                  </div>
                </td>
              </>
            )}
            {isInResolutionWithoutPlan && (
              <td>
                <div className="is-flex is-align-items-end">
                  <button
                    type="button"
                    data-testid="payment-plan-create-resolution"
                    className="button is-link is-small mr-1"
                    onClick={() => getCurrentPaymentInfo("resolution")}
                  >
                    <span className="mx-3">Create Resolution Plan <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={`This option is only available if there has been a settlement with the tribe`}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-light" />
                    </span></span>
                  </button>
                </div>
              </td>
            )}
            <td></td>
            <td>
              <div className="has-text-right">
                <button
                  type="button"
                  data-testid="step1-cancel"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => checkChangesMade()}
                >
                  <span className="mx-3">Cancel</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {strategy?.flow === 'wizard' && (
        <form onSubmit={handleSubmit(onSubmit)} id="payment_plan_payment_form">
          <div className="divider m-0 mx-1"></div>

          <table className="table is-narrow table-payment-schedule-panel">
            <tbody>
              <tr>
                <td>When would you like to make your payments?</td>
                <td style={{ minWidth: "180px" }} data-testid="step1-pay-frequency">
                  <Controller
                    name="pay_frequency_dropdown_option_id"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <LoxSelect
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={payFrequencyOptions}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        isSearchable={false}
                        onChange={(val) => {
                          field.onChange({ label: val?.label, value: val?.value });
                          setPayFrequencyValue(val?.label);
                        }}
                      />
                    )}
                  />
                </td>

                {payFrequencyValue == "Weekly" && (
                  <>
                    <td>Pay Day Of Week</td>
                    <td>
                      <Controller
                        name="pay_first_day_of_week_dropdown_option_id"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={dayOfWeekOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </>
                )}
                {payFrequencyValue == "Bi-Weekly" && (
                  <>
                    <td></td>
                    <td></td>
                    <td></td>
                  </>
                )}
                {payFrequencyValue == "Semi-Monthly" && (
                  <>
                    <td>Semi Monthly Option</td>
                    <td data-testid="step1-semi-monthly-option">
                      <Controller
                        name="selection_semi_monthly"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={[{ label: 'Specific Days', value: 'Specific Days' }, { label: 'Week Day and Week of Month', value: 'Week Day and Week of Month' }]}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                            styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                            onChange={(val) => {
                              field.onChange({ label: val?.label, value: val?.value });
                              setPayFrequencyValueOption(val?.label);
                            }}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </>
                )}
                {payFrequencyValue == "Monthly" && (
                  <>
                    <td>Monthly Option</td>
                    <td data-testid="step1-monthly-option">
                      <Controller
                        name="selection_monthly"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={[{ label: 'Pay Day of Month', value: 'Pay Day of Month' }, { label: 'Week Day and Week of Month', value: 'Week Day and Week of Month' }]}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                            styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                            onChange={(val) => {
                              field.onChange({ label: val?.label, value: val?.value });
                              setPayFrequencyValueOption(val?.label);
                            }}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </>
                )}
                {payFrequencyValue == "" && (
                  <td colSpan={3}></td>
                )}
              </tr>

              {payFrequencyValue == "Semi-Monthly" && payFrequencyValueOption == "Specific Days" && (
                <>
                  <tr>
                    <td colSpan={2}></td>
                    <td>Day 1</td>
                    <td style={{ minWidth: "180px" }} data-testid="step1-semi-monthly-day-1">
                      <Controller
                        name="semi_monthly_selection1_day_1"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={dayOfMonthOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={2}></td>
                    <td>Day 2</td>
                    <td style={{ minWidth: "180px" }} data-testid="step1-semi-monthly-day-2">
                      <Controller
                        name="semi_monthly_selection1_day_2"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={dayOfMonthOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </tr>
                </>
              )}

              {payFrequencyValue == "Semi-Monthly" && payFrequencyValueOption == "Week Day and Week of Month" && (
                <>
                  <tr>
                    <td colSpan={2}></td>
                    <td style={{ minWidth: "180px" }}>
                      <Controller
                        name="semi_monthly_selection2_week_1"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={weekOfMonthOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td style={{ minWidth: "180px" }}>
                      <Controller
                        name="semi_monthly_selection2_day_1"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={dayOfWeekOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={2}></td>
                    <td style={{ minWidth: "180px" }}>
                      <Controller
                        name="semi_monthly_selection2_week_2"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={weekOfMonthOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td style={{ minWidth: "180px" }}>
                      <Controller
                        name="semi_monthly_selection2_day_2"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={dayOfWeekOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                          />
                        )}
                      />
                    </td>
                    <td></td>
                  </tr>
                </>
              )}

              {payFrequencyValue == "Monthly" && payFrequencyValueOption == "Pay Day of Month" && (
                <tr>
                  <td colSpan={2}></td>
                  <td>Day</td>
                  <td style={{ minWidth: "180px" }} data-testid="step1-pay-day-of-month">
                    <Controller
                      name="monthly_selection1_day"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          classNames={reactSelectClassNames()}
                          options={dayOfMonthOptions}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          isSearchable={false}
                        />
                      )}
                    />
                  </td>
                  <td></td>
                </tr>
              )}

              {payFrequencyValue == "Monthly" && payFrequencyValueOption == "Week Day and Week of Month" && (
                <tr>
                  <td colSpan={2}></td>
                  <td style={{ minWidth: "180px" }}>
                    <Controller
                      name="monthly_selection2_week_1"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          classNames={reactSelectClassNames()}
                          options={weekOfMonthOptions}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          isSearchable={false}
                        />
                      )}
                    />
                  </td>
                  <td style={{ minWidth: "180px" }}>
                    <Controller
                      name="monthly_selection2_day_1"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          classNames={reactSelectClassNames()}
                          options={dayOfWeekOptions}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          isSearchable={false}
                        />
                      )}
                    />
                  </td>
                  <td></td>
                </tr>
              )}

              <tr>
                <td>If your payday falls on a holiday or weekend,<br />do you get paid the previous business day or the next business day?</td>
                <td data-testid="step1-pay-before-holiday">
                  <Controller
                    name="is_pay_before_holiday"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <LoxSelect
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={[{ label: 'Before', value: 1 }, { label: 'After', value: 0 }]}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        isSearchable={false}
                      />
                    )}
                  />
                </td>
                <td>Payment Method</td>
                <td style={{ minWidth: "200px" }} data-testid="step1-payment-method">
                  <Controller
                    name="payment_method_dropdown_option_id_step1"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <LoxSelect
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={discountData?.payment_method_dropdown_options.map((o) => ({ label: o.name, value: o.id }))}
                        menuPosition="fixed"
                        menuPlacement="auto"
                        isSearchable={false}
                      />
                    )}
                  />
                </td>
                <td></td>
              </tr>

              <tr>
                <td>First Payment?</td>
                <td data-testid="step1-first-payment">
                  <Controller
                    name="first_payment"
                    rules={{ required: true }}
                    control={control}
                    defaultValue={currentDate ? currentDate.split(" ")[0] : null}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="date"
                        data-testid="step1-first-payment-input"
                        min={currentDate ? currentDate.split(" ")[0] : today}
                        value={field.value || ""}
                      />
                    )}
                  />
                </td>
                <td colSpan={2}></td>
                <td></td>
              </tr>

              {(strategy?.includesPromo && collectionPromo?.id) && (
                  <tr data-testid="step1-promotion-name-row">
                    <td>Promotion Name</td>
                    <td style={{ minWidth: "200px" }}>
                      <Controller
                        name="customer_promotion_code_id"
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            options={[{ label: collectionPromo?.promotion?.name, value: collectionPromo?.id }]}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                            isClearable
                            styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                            onChange={(val) => {
                              field.onChange({ label: val?.label, value: val?.value });
                              if (val) {
                                setValue('discount_percent_step1', collectionPromo?.promotion?.promo_percent);
                              }
                              setSettlementDiscountType('percent');
                            }}
                          />
                        )}
                      />
                    </td>
                    <td colSpan={2}></td>
                    <td></td>
                  </tr>
              )}

              {strategy?.includesDiscount && (
                <tr data-testid="step1-settlement-discount-row">
                  <td className="pt-5">
                    <b>Settlement Discount <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={`Agent's Maximum: ${discountData?.max_discount_percent?.agent}%/$${discountData?.max_discount_amount?.agent}\nManager's Maximum: ${discountData?.max_discount_percent?.manager}%/$${discountData?.max_discount_amount?.manager}`}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span></b>
                    <div>
                      <div className="control">
                        <label className="radio mr-3">
                          <input name="settlement_discount_type" data-testid="step1-discount-type-percent" value="percent" type="radio" onChange={() => handleDiscountTypeChange("percent")} checked={settlementDiscountType === "percent"} disabled={watchCustomerPromotionCodeId?.value} /> Percent (%)
                        </label>
                        OR
                        <label className="radio ml-3">
                          <input name="settlement_discount_type" data-testid="step1-discount-type-amount" value="amount" type="radio" onChange={() => handleDiscountTypeChange("amount")} checked={settlementDiscountType === "amount"} disabled={watchCustomerPromotionCodeId?.value} /> Amount ($)
                        </label>
                      </div>
                    </div>
                    <div style={{ maxWidth: "200px" }}>
                      {settlementDiscountType == "percent" && (
                        <Controller
                          name="discount_percent_step1"
                          rules={{ required: true }}
                          control={control}
                          defaultValue={discountData?.max_discount_percent?.agent}
                          render={({ field }) => (
                            <input
                              {...field}
                              data-testid="step1-discount-percent-input"
                              className="input is-small"
                              type="number"
                              min={0}
                              max={discountData?.max_discount_percent?.manager}
                              step="0.01"
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e);
                                setDiscountValue(e.target.value);
                              }}
                              disabled={watchCustomerPromotionCodeId?.value}
                            />
                          )}
                        />
                      )}
                      {settlementDiscountType == "amount" && (
                        <Controller
                          name="discount_amount_step1"
                          rules={{ required: true }}
                          control={control}
                          defaultValue={discountData?.max_discount_amount?.agent}
                          render={({ field }) => (
                            <input
                              {...field}
                              data-testid="step1-discount-amount-input"
                              className="input is-small"
                              type="number"
                              min={0}
                              max={discountData?.max_discount_amount?.manager}
                              step="0.01"
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e);
                                setDiscountValue(e.target.value);
                              }}
                            />
                          )}
                        />
                      )}
                    </div>
                    {/* Constrain the message so a long one wraps instead of widening the
                        column and shifting the rest of the table (e.g. the Cancel/Next buttons) */}
                    <p data-testid="step1-discount-message" style={{ maxWidth: "250px" }}>{discountInfoText}</p>
                  </td>
                  <td colSpan={4}></td>
                </tr>
              )}

              <tr>
                <td className="pt-5" style={{ zIndex: 15, position: 'relative' }}>
                  <b data-testid="step1-breakdown-label">{strategy?.breakdownLabel} <span
                    className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                    data-tooltip={breakdownTooltip}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span></b>
                  <div>
                    <div className="control">
                      <label className="radio mr-3">
                        <input name="settlement_installation_type" value="number_of_installments" type="radio" onChange={() => setSettlementInstallationType("number_of_installments")} checked={`${settlementInstallationType === "number_of_installments" ? "checked" : ""}`} disabled={isDiscountAboveCap} /> # of Installments
                      </label>
                      OR
                      <label className="radio ml-3">
                        <input name="settlement_installation_type" value="amount" type="radio" onChange={() => setSettlementInstallationType("amount")} checked={`${settlementInstallationType === "amount" ? "checked" : ""}`} disabled={isDiscountAboveCap} /> Amount ($)
                      </label>
                    </div>
                  </div>
                  <div style={{ maxWidth: "200px" }} data-testid="step1-number-of-installments">
                    {settlementInstallationType == "number_of_installments" && (
                      <Controller
                        name="number_of_installments_step1"
                        control={control}
                        render={({ field }) => (
                          <LoxSelect
                            {...field}
                            classNames={reactSelectClassNames()}
                            key={payFrequencyValue}
                            options={installmentOptions}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            isSearchable={false}
                            isClearable
                            isDisabled={!installmentOptions || isDiscountAboveCap}
                            inputId="payment-plan-installments"
                            onChange={(e) => {
                              field.onChange(e); // call the original onChange from react-hook-form
                            }}
                            noOptionsMessage={({ inputValue }) => (!inputValue ? "Select pay frequency first" : "No results found")}
                          />
                        )}
                      />
                    )}
                    {settlementInstallationType == "amount" && (
                      <Controller
                        name="installment_amount"
                        control={control}
                        defaultValue={""}
                        render={({ field }) => (
                          <input
                            {...field}
                            className="input is-small"
                            type="number"
                            min={0}
                            step="0.01"
                            value={field.value || ""}
                            disabled={isDiscountAboveCap}
                          />
                        )}
                      />
                    )}
                  </div>
                </td>
                <td colSpan={4}></td>
              </tr>

              <tr>
                <td data-testid="step1-min-payment">Min Payment Amount: {minPayment != null ? `$${minPayment}` : '—'}</td>
              </tr>

              {balanceBelowMinimumText && (
                <tr>
                  <td colSpan={5} className="has-text-danger" data-testid="step1-balance-below-minimum">
                    {balanceBelowMinimumText}
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan={4}></td>
                <td>
                  <div className="has-text-right">
                    <button
                      type="button"
                      className="button has-background-grey has-text-white is-small mr-1"
                      onClick={() => checkChangesMade()}
                    >
                      <span className="mx-3">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      data-testid="step1-next"
                      className="button is-link is-small mr-1"
                      disabled={saving || isDiscountAboveCap || isBalanceBelowMinimum}
                    >
                      <span className="mx-3">{saving ? 'Submitting' : 'Next'}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      )}

      {strategy?.flow === 'single-step' && (
        <form onSubmit={handleSubmit(onSettleForZeroSubmit)} id="payment_plan_payment_settle_for_zero_form">
          <div className="divider m-0 mx-1"></div>

          <div data-testid="step1-settle-for-zero-confirm-copy">
            <SpecialInstruction
              content={"<span>" +
                "<b>Are you sure you want to close this loan with a settlement for $0?</b><br/><br/>" +
                "<span class='has-text-weight-bold'>This will perform the following actions:</span<br>" +
                "<span class='pl-2'>• Cancel any existing payment/settlement plans</span> <br> " +
                "<span class='pl-2'>• Create a payoff discounted to $0</span> <br> " +
                "<span class='pl-2'>• Report to Credit Bureaus as 'loan closed for less than the owed balance'</span> <br> " +
                " </span>"}
              type="is-danger"
            />
          </div>

          <table className="table is-narrow table-payment-schedule-panel">
            <tbody>
              <tr>
                <td colSpan={4}></td>
                <td>
                  <div className="has-text-right">
                    <button
                      type="button"
                      data-testid="step1-settle-for-zero-cancel"
                      className="button has-background-grey has-text-white is-small mr-1"
                      onClick={() => checkChangesMade()}
                    >
                      <span className="mx-3">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      data-testid="step1-settle-for-zero-submit"
                      className="button is-link is-small mr-1"
                      disabled={saving && 'disabled'}
                    >
                      <span className="mx-3">{saving ? 'Settling' : 'Settle'}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      )}
    </>
  )
}
